import {useCallback, useEffect, useState} from 'react'
import Loader from '../../custom/loader/Loader'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from '../../../redux/hooks'
import {LecturerNews} from '../../../redux/Slice/lecturerSlice'
import {checkEmergencyContact} from '../../../redux/Slice/studentSlice'
import EmergencyContact from './EmergencyContact'
import {Link} from 'react-router-dom'

const LecturerDashboard = () => {
  const [loader, setLoader] = useState(true)
  const {t} = useTranslation()
  const dispatch = useAppDispatch()
  const [news, setNews] = useState<any[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)
  const [isShown, setIsShown] = useState(false)
  const toggle = useCallback(() => setIsShown((prev) => !prev), [])

  const [openNews, setOpenNews] = useState<number[]>([])

  useEffect(() => {
    dispatch(LecturerNews()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setNews(res.payload)
        setLoader(false)
      }
    })
    dispatch(checkEmergencyContact()).then((res: any) => {
      if (res.meta.requestStatus === 'fulfilled') {
        if (res.payload.has_emergency_contact === false) toggle()
      }
    })
  }, [])

  useEffect(() => {
    setCurrentPage(1)
    setOpenNews([]) // reset open state when page changes
  }, [news, itemsPerPage])

  const formatDate = (dateString: string) => {
    if (!dateString) return ''
    const [datePart, timePart] = dateString.split(' ')
    const [day, month, year] = datePart.split('-')
    const formatted = `${year}-${month}-${day}T${timePart}`

    return new Date(formatted).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }

  if (loader) return <Loader />

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = news.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(news.length / itemsPerPage)

  return (
    <>
      <div className='row mb-5'>
        <div className='col-3'>
          <Link to={'/lec/my-profile'} className='text-decoration-none'>
            <div className='card bg-primary shadow rounded-4 h-100 dashboard-card text-white'>
              <div className='card-body d-flex justify-content-between align-items-center p-4'>
                <div className='d-flex align-items-center'>
                  <span className='fas fa-user mx-3' style={{fontSize: '3rem'}}></span>
                  <span className='fs-3'>{t('LABEL.MY_PROFILE')}</span>
                </div>
              </div>
            </div>
          </Link>
        </div>
        <div className='col-3'>
          <Link to={'/lec/my-courses'} className='text-decoration-none'>
            <div className='card bg-danger shadow rounded-4 h-100 dashboard-card text-white'>
              <div className='card-body d-flex justify-content-between align-items-center p-4'>
                <div className='d-flex align-items-center'>
                  <span className='fas fa-chalkboard-teacher mx-3' style={{fontSize: '3rem'}}></span>
                  <span className='fs-3'>{t('LABEL.MY_COURSES')}</span>
                </div>
              </div>
            </div>
          </Link>
        </div>
        <div className='col-3'>
          <Link to={'/lec/publications-list'} className='text-decoration-none'>
            <div className='card bg-primary-active shadow rounded-4 h-100 dashboard-card text-white'>
              <div className='card-body d-flex justify-content-between align-items-center p-4'>
                <div className='d-flex align-items-center'>
                  <span className='fas fa-book-open mx-3' style={{fontSize: '3rem'}}></span>
                  <span className='fs-3'>{t('MENU.PUBLICATIONS')}</span>
                </div>
              </div>
            </div>
          </Link>
        </div>
        <div className='col-3'>
          <Link to={'/lec/meetings'} className='text-decoration-none'>
            <div className='card bg-info shadow rounded-4 h-100 dashboard-card text-white'>
              <div className='card-body d-flex justify-content-between align-items-center p-4'>
                <div className='d-flex align-items-center'>
                  <span className='fas fa-calendar mx-3' style={{fontSize: '3rem'}}></span>
                  <span className='fs-3'>{t('MEETING.MEETINGS')}</span>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>
      <div id='print-news-section'>
        <div className='container-fluid p-0'>
          <div className='card mb-3 p-0'>
            <div className='card-header border-0 bg-white py-4 rounded-top-4 d-flex justify-content-between align-items-center'>
              <h3 className='fw-bold text-primary m-0'>{t('LABEL.NEWS_AND_ANNOUNCEMENT')}</h3>

              <div className='d-flex align-items-center gap-3 p-none' style={{minWidth: '400px'}}>
                <button
                  className='btn btn-sm btn-outline-primary p-none'
                  onClick={() => {
                    if (openNews.length === currentItems.length) {
                      setOpenNews([])
                    } else {
                      setOpenNews(currentItems.map((item: any) => item.id))
                    }
                  }}
                >
                  <span
                    className={`mx-2 fas ${
                      openNews.length === currentItems.length ? 'fa-chevron-up' : 'fa-chevron-down'
                    }`}
                  ></span>
                  {openNews.length === currentItems.length
                    ? t('LABEL.COLLAPSE_ALL')
                    : t('LABEL.EXPAND_ALL')}
                </button>

                <button
                  className='btn btn-sm btn-primary p-none'
                  onClick={() => {
                    const printContents = document.getElementById('print-news-section')?.innerHTML
                    const originalContents = document.body.innerHTML

                    if (printContents) {
                      document.body.innerHTML = printContents
                      window.print()
                      document.body.innerHTML = originalContents
                      window.location.reload()
                    }
                  }}
                >
                  <span className='fas fa-print mx-2'></span>
                  {t('BUTTONS.PRINT')}
                </button>

                {/* RECORDS PER PAGE */}
                <div className='d-flex align-items-center p-none' style={{minWidth: '200px'}}>
                  <label className='mt-2 mx-2 fw-bold text-nowrap p-none'>
                    {t('GLOBAL.RECORD_PER_PAGE')}
                  </label>
                  <select
                    className='form-select form-select-sm p-none'
                    style={{width: '100px'}}
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PRINTABLE SECTION */}
        <div className='my-4'>
          {currentItems.length > 0 &&
            currentItems.map((item: any) => (
              <div key={item.id} className='card mb-3 shadow-sm rounded-4'>
                <div
                  className={`card-header d-flex flex-column justify-content-center bg-${
                    item.priority === 'Normal' ? 'primary' : 'danger'
                  }`}
                  style={{minHeight: '40px', cursor: 'pointer'}}
                  onClick={() => {
                    if (openNews.includes(item.id)) {
                      setOpenNews(openNews.filter((id) => id !== item.id))
                    } else {
                      setOpenNews([...openNews, item.id])
                    }
                  }}
                >
                  <h5 className='card-title fw-bold text-white mb-0'>{item.title}</h5>
                  <small className='text-light mx-3 fw-bold mb-1'>
                    <i className='fas fa-calendar-alt mx-1'></i>
                    {formatDate(item.date)}
                  </small>
                </div>

                {openNews.includes(item.id) && (
                  <div className='card-body'>
                    <p
                      dangerouslySetInnerHTML={{
                        __html: item.content,
                      }}
                    ></p>
                  </div>
                )}
              </div>
            ))}

          {totalPages > 1 && (
            <nav className='p-none'>
              <ul className='pagination justify-content-center mt-4'>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button className='page-link' onClick={() => setCurrentPage(currentPage - 1)}>
                    {t('LABEL.PREVIOUS')}
                  </button>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li
                    key={index}
                    className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                  >
                    <button className='page-link' onClick={() => setCurrentPage(index + 1)}>
                      {index + 1}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button className='page-link' onClick={() => setCurrentPage(currentPage + 1)}>
                    {t('LABEL.NEXT')}
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
        {isShown && <EmergencyContact modelType={'Lecturer'} />}
      </div>
    </>
  )
}

export default LecturerDashboard
